import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { businessApi, adminApi, translateApi, platformApi, BASE_URL } from '../api/apiClient';
import AnalyticsPanel from '../components/AnalyticsPanel';
import { BusinessLangProvider, useBusinessT, BIZ_UI_SECTIONS, BIZ_UI_DEFAULTS } from '../context/BusinessLangContext';
import { LANGUAGES } from '../context/LanguageContext';
import { PAYOUT_COUNTRIES, PAYOUT_FIELDS, methodForCountry } from '../utils/payout';

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────
const slugify = s => s.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
const notify = (set, msg, type = 'success') => {
  set({ msg, type, id: Date.now() });
  setTimeout(() => set(null), 3500);
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Dashboard
// ─────────────────────────────────────────────────────────────────────────────
// Wrapper: provides the dashboard-language context (separate from client language).
// Map a plan's feature flags to human-readable bullet lines for the pricing cards.
const FEATURE_LINES = {
  branches: 'Multi-branch', events: 'Events & promotions', aiTools: 'AI tools',
  advancedAnalytics: 'Advanced analytics', multiLanguage: 'Multi-language menu',
  customDomain: 'Custom domain', removeBranding: 'Remove “Powered by”', aiBlog: 'AI daily SEO blog', prioritySupport: 'Priority support',
};
function planFeatureList(p) {
  const f = p.features || {};
  const out = [];
  out.push(f.maxRestaurants === -1 ? 'Unlimited restaurants' : `${f.maxRestaurants || 1} restaurant${(f.maxRestaurants||1) === 1 ? '' : 's'}`);
  out.push(f.maxProducts === -1 ? 'Unlimited products' : `Up to ${f.maxProducts || 50} products`);
  Object.keys(FEATURE_LINES).forEach(k => { if (f[k]) out.push(FEATURE_LINES[k]); });
  return out;
}
const DEFAULT_PLAN_CARDS = [
  { id:'basic', name:'Basic', price:29, currency:'USD', interval:'mo', highlight:false, active:true, tagline:'For a single venue', features:{ maxRestaurants:1, maxProducts:50 } },
  { id:'pro', name:'Pro', price:79, currency:'USD', interval:'mo', highlight:true, active:true, tagline:'For growing chains', features:{ maxRestaurants:-1, maxProducts:-1, branches:true, events:true, aiTools:true, advancedAnalytics:true, multiLanguage:true, customDomain:true, prioritySupport:true } },
  { id:'enterprise', name:'Enterprise', price:199, currency:'USD', interval:'mo', highlight:false, active:true, tagline:'White-label', features:{ maxRestaurants:-1, maxProducts:-1, branches:true, events:true, aiTools:true, advancedAnalytics:true, multiLanguage:true, customDomain:true, removeBranding:true, prioritySupport:true } },
];

export default function BusinessDashboard() {
  const { businessOwner } = useAuth();
  return (
    <BusinessLangProvider
      lang={businessOwner?.dashboardLang || 'en'}
      translations={businessOwner?.dashboardTranslations || {}}
    >
      <BusinessDashboardInner />
    </BusinessLangProvider>
  );
}

function BusinessDashboardInner() {
  const navigate = useNavigate();
  const { businessOwner, logoutBusinessOwner, syncOwnerProfile } = useAuth();
  const { bt, dir } = useBusinessT();

  const [restaurants,   setRestaurants]   = useState(businessOwner?.restaurants || []);
  const [activeTab,     setActiveTab]     = useState('restaurants');
  const [toast,         setToast]         = useState(null);
  const [addRestModal,    setAddRestModal]    = useState(false);
  const [branchModal,     setBranchModal]     = useState(null); // { restaurant }
  const [editRestModal,   setEditRestModal]   = useState(null); // { restaurant }
  const [adminCredModal,  setAdminCredModal]  = useState(null); // { restaurant }
  const [saving,          setSaving]          = useState(false);
  const [analyticsSlug,   setAnalyticsSlug]   = useState(null); // null = first restaurant
  const [plans,           setPlans]           = useState(null); // platform-defined plans
  const [payout,          setPayout]          = useState(businessOwner?.payout || { country: '', method: '' });
  useEffect(() => { platformApi.getSettings().then(r => setPlans(r.data?.plans || null)).catch(() => {}); }, []);
  const [growth, setGrowth] = useState(null);
  useEffect(() => { businessApi.getGrowth().then(r => setGrowth(r.data)).catch(() => {}); }, []);
  const copyReferral = () => {
    const link = growth?.referral?.link;
    if (!link) return;
    try { navigator.clipboard.writeText(link); showToast(bt('biz_ref_copied')); }
    catch { showToast(link); }
  };
  const savePayout = async () => {
    setSaving(true);
    try {
      const method = methodForCountry(payout.country);
      const r = await businessApi.updateMe({ payout: { ...payout, method } });
      if (r.data) syncOwnerProfile?.(r.data);
      showToast(bt('biz_payout_saved'));
    } catch (e) { showToast(e.message || bt('biz_t_update_failed'), 'error'); }
    finally { setSaving(false); }
  };

  // ── Account form ───────────────────────────────────────────────────────────
  const [acct, setAcct] = useState({ ownerName: businessOwner?.ownerName || '', phone: businessOwner?.phone || '' });
  const [pass, setPass] = useState({ current: '', next: '', confirm: '' });
  const setA = k => e => setAcct(f => ({ ...f, [k]: e.target.value }));
  const setP = k => e => setPass(f => ({ ...f, [k]: e.target.value }));

  const saveAccount = async () => {
    try {
      const r = await businessApi.updateMe({ ownerName: acct.ownerName, phone: acct.phone });
      syncOwnerProfile({ ownerName: acct.ownerName, phone: acct.phone });
      showToast(bt('biz_t_account_updated'));
    } catch (e) { showToast(e.message || bt('biz_t_update_failed'), 'error'); }
  };
  const savePassword = async () => {
    if (!pass.current || !pass.next) { showToast(bt('biz_t_enter_passwords'), 'error'); return; }
    if (pass.next !== pass.confirm) { showToast(bt('biz_t_pass_mismatch'), 'error'); return; }
    if (pass.next.length < 6) { showToast(bt('biz_t_pass_min6'), 'error'); return; }
    try {
      await businessApi.updateMe({ password: pass.current, newPassword: pass.next });
      setPass({ current: '', next: '', confirm: '' });
      showToast(bt('biz_t_pass_updated'));
    } catch (e) { showToast(e.message || bt('biz_t_update_failed'), 'error'); }
  };

  // ── Dashboard language (per-account, separate from client language) ─────────
  const [dashLang,   setDashLang]   = useState(businessOwner?.dashboardLang || 'en');
  const [dashTrans,  setDashTrans]  = useState(businessOwner?.dashboardTranslations || {});
  const [dashBusy,   setDashBusy]   = useState(false);

  // Auto-translate every dashboard string into the chosen language (API → AI
  // fallback on the server), then persist on the owner account.
  const saveDashboardLanguage = async (targetLang) => {
    setDashBusy(true);
    try {
      let translations = {};
      if (targetLang !== 'en') {
        const keys = [], texts = [];
        BIZ_UI_SECTIONS.forEach(s => s.keys.forEach(({ key, default: def }) => { keys.push(key); texts.push(def); }));
        const res = await translateApi.translate(texts, 'en', targetLang);
        const out = res?.translations || [];
        keys.forEach((k, i) => { if (out[i] && out[i].trim()) translations[k] = out[i]; });
      }
      await businessApi.updateMe({ dashboardLang: targetLang, dashboardTranslations: translations });
      setDashLang(targetLang);
      setDashTrans(translations);
      syncOwnerProfile({ dashboardLang: targetLang, dashboardTranslations: translations });
      showToast(targetLang === 'en' ? bt('biz_t_lang_en') : bt('biz_t_lang_changed') + ' — ' + (LANGUAGES[targetLang]?.name || targetLang));
    } catch (e) {
      showToast(e.message || bt('biz_t_lang_failed'), 'error');
    } finally { setDashBusy(false); }
  };

  // Fetch up-to-date restaurants on mount
  useEffect(() => {
    if (!businessOwner) return;
    businessApi.getRestaurants()
      .then(r => setRestaurants(r.data || []))
      .catch(() => {});
  }, [businessOwner]);

  if (!businessOwner) { navigate('/business/login'); return null; }

  const handleLogout = () => { logoutBusinessOwner(); navigate('/business/login'); };
  const showToast = (msg, type) => notify(setToast, msg, type);

  const refreshRestaurants = async () => {
    try {
      const r = await businessApi.getRestaurants();
      setRestaurants(r.data || []);
    } catch {}
  };

  const handleAddRestaurant = async (form) => {
    setSaving(true);
    try {
      await businessApi.addRestaurant(form);
      await refreshRestaurants();
      setAddRestModal(false);
      showToast(bt('biz_t_rest_created'));
    } catch (e) {
      showToast(e.message || bt('biz_t_create_failed'), 'error');
    } finally { setSaving(false); }
  };

  const handleEditRestaurant = async (restaurantId, form) => {
    setSaving(true);
    try {
      await businessApi.updateRestaurant(restaurantId, form);
      await refreshRestaurants();
      setEditRestModal(null);
      showToast(bt('biz_t_rest_updated'));
    } catch (e) {
      showToast(e.message || bt('biz_t_update_failed'), 'error');
    } finally { setSaving(false); }
  };

  const handleDeleteRestaurant = async (restaurantId, name) => {
    if (!window.confirm(`Delete "${name}"? This will permanently delete the restaurant and all its data. This cannot be undone.`)) return;
    try {
      await businessApi.deleteRestaurant(restaurantId);
      await refreshRestaurants();
      showToast(`"${name}" `+bt("biz_t_deleted"));
    } catch (e) {
      showToast(e.message || bt('biz_t_update_failed'), 'error');
    }
  };

  const handleUpdateAdminCreds = async (restaurantId, form) => {
    setSaving(true);
    try {
      await businessApi.updateAdminCredentials(restaurantId, form);
      setAdminCredModal(null);
      showToast(bt('biz_t_creds_updated'));
    } catch (e) {
      showToast(e.message || bt('biz_t_update_failed'), 'error');
    } finally { setSaving(false); }
  };

  const tabs = [
    { id: 'restaurants', label: '🏪', full: bt('biz_restaurants') },
    { id: 'analytics',   label: '📈', full: bt('biz_analytics')   },
    { id: 'grow',        label: '🚀', full: bt('biz_grow')        },
    { id: 'subscription',label: '💎', full: bt('biz_plans')       },
    { id: 'payout',      label: '💳', full: bt('biz_payout')      },
    { id: 'account',     label: '👤', full: bt('biz_account')     },
    { id: 'language',    label: '🌐', full: bt('biz_language')    },
  ];

  return (
    <div className="bd-root" dir={dir}>
      {/* ── Toast ── */}
      {toast && (
        <div className={`bd-toast ${toast.type}`} key={toast.id}>
          {toast.type === 'success' ? '✓' : '✗'} {toast.msg}
        </div>
      )}

      {/* ── Sidebar ── */}
      <aside className="bd-sidebar">
        <div className="bd-sidebar-logo">
          <span className="bd-logo-star">✦</span>
          <span>Nooshin</span>
        </div>
        <div className="bd-sidebar-owner">
          <div className="bd-owner-avatar">{(businessOwner.ownerName || 'O')[0].toUpperCase()}</div>
          <div>
            <div className="bd-owner-name">{businessOwner.ownerName || 'Owner'}</div>
            <div className="bd-owner-plan">{businessOwner.plan || 'Basic'} Plan</div>
          </div>
        </div>
        <nav className="bd-nav">
          {tabs.map(t => (
            <button key={t.id} className={`bd-nav-btn ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => setActiveTab(t.id)}>
              <span className="bd-nav-icon">{t.label}</span>
              <span>{t.full}</span>
            </button>
          ))}
        </nav>
        <button className="bd-sidebar-logout" onClick={handleLogout}>
          <span>↩</span> {bt('biz_logout')}
        </button>
      </aside>

      {/* ── Main Content ── */}
      <main className="bd-main">

        {/* ── MY RESTAURANTS ── */}
        {activeTab === 'restaurants' && (
          <div className="bd-page">
            <div className="bd-page-header">
              <div>
                <h1 className="bd-page-title">{bt('biz_rest_title')}</h1>
                <p className="bd-page-sub">{bt('biz_rest_sub')}</p>
              </div>
              <button className="bd-add-rest-btn" onClick={() => setAddRestModal(true)}>
                {bt('biz_new_rest')}
              </button>
            </div>

            {restaurants.length === 0 ? (
              <div className="bd-empty">
                <div className="bd-empty-icon">🏪</div>
                <h3>{bt('biz_no_rest')}</h3>
                <p>{bt('biz_no_rest_sub')}</p>
                <button className="bd-add-rest-btn" onClick={() => setAddRestModal(true)}>
                  {bt('biz_create_rest')}
                </button>
              </div>
            ) : (
              <div className="bd-rest-grid">
                {restaurants.map(rest => (
                  <RestaurantCard
                    key={rest._id || rest.slug}
                    restaurant={rest}
                    onManageBranches={() => setBranchModal({ restaurant: rest })}
                    onEdit={() => setEditRestModal({ restaurant: rest })}
                    onDelete={() => handleDeleteRestaurant(rest._id, rest.name)}
                    onAdminCreds={() => setAdminCredModal({ restaurant: rest })}
                    onRefresh={refreshRestaurants}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── ANALYTICS ── */}
        {activeTab === 'analytics' && (() => {
          if (!restaurants.length) return (
            <div className="bd-page"><div className="bd-page-header"><h1 className="bd-page-title">{bt('biz_analytics')}</h1></div>
              <div style={{padding:'2rem',textAlign:'center',color:'rgba(255,255,255,.35)'}}>{bt('biz_analytics_empty')}</div>
            </div>
          );
          const selectedRest = (analyticsSlug && restaurants.find(r=>r.slug===analyticsSlug)) || restaurants[0];
          const slug = selectedRest.slug;
          const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api';
          return (
            <div className="bd-page">
              <div className="bd-page-header" style={{flexWrap:'wrap',gap:'.6rem'}}>
                <div>
                  <h1 className="bd-page-title">{bt('biz_analytics_title')}</h1>
                  <p className="bd-page-sub">{bt('biz_analytics_sub')}</p>
                </div>
                {restaurants.length > 1 && (
                  <select
                    value={analyticsSlug || restaurants[0].slug}
                    onChange={e => setAnalyticsSlug(e.target.value)}
                    style={{padding:'.4rem .7rem',background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.1)',borderRadius:8,color:'#fff',fontFamily:"'DM Sans',sans-serif",fontSize:'.8rem',cursor:'pointer'}}>
                    {restaurants.map(r => <option key={r._id} value={r.slug}>{r.name}</option>)}
                  </select>
                )}
              </div>
              <AnalyticsPanel
                key={slug}
                slug={slug}
                scope="restaurant"
                currencyCode={selectedRest.currencyCode || 'USD'}
                fetchData={async (days) => adminApi.getAnalytics(slug, days)}
                fetchBehavior={async (days) => adminApi.getBehavior(slug, days)}
                fetchLogs={async (params) => adminApi.getAuditLogs(slug, params)}
                aiAnalyze={async (messages) => {
                  const token = localStorage.getItem('nooshin_admin_token');
                  const r = await fetch(`${API_BASE}/ai-analyze`, {
                    method:'POST',
                    headers:{'Content-Type':'application/json','Authorization':`Bearer ${token}`},
                    body: JSON.stringify({ messages, restaurantSlug:slug, scope:'restaurant' }),
                  });
                  return r.json();
                }}
                exportAuditUrl={(params) => {
                  const token = localStorage.getItem('nooshin_admin_token');
                  return `${API_BASE}/admin/${slug}/audit/export.csv?${params}&token=${token}`;
                }}
              />
            </div>
          );
        })()}

        {/* ── SUBSCRIPTION ── */}
        {activeTab === 'grow' && (() => {
          const a = growth?.access || {};
          const ref = growth?.referral || {};
          const statusLabel = a.status === 'active' ? bt('biz_grow_active')
            : a.status === 'free' ? bt('biz_grow_free')
            : a.status === 'expired' ? bt('biz_grow_expired')
            : bt('biz_grow_trial');
          return (
          <div className="bd-page">
            <div className="bd-page-header">
              <div>
                <h1 className="bd-page-title">🚀 {bt('biz_grow_title')}</h1>
                <p className="bd-page-sub">{bt('biz_grow_sub')}</p>
              </div>
            </div>

            {/* Trial / status banner */}
            <div className="bd-card" style={{padding:'1.5rem',marginBottom:'1.25rem',borderRadius:16,
              background: a.expired ? 'rgba(239,68,68,.08)' : 'rgba(34,197,94,.07)',
              border:`1px solid ${a.expired ? 'rgba(239,68,68,.3)' : 'rgba(34,197,94,.25)'}`}}>
              <div style={{fontSize:'.85rem',opacity:.7,marginBottom:'.35rem'}}>{statusLabel}</div>
              {a.status === 'trial' && (
                <div style={{fontSize:'1.4rem',fontWeight:800}}>
                  {a.daysLeft} {bt('biz_grow_days_left')}
                </div>
              )}
              {a.status === 'active' && <div style={{fontSize:'1.2rem',fontWeight:700}}>✓ {growth?.plan}</div>}
              {(a.status === 'free' || a.status === 'expired') && (
                <button className="bd-plan-btn primary" style={{marginTop:'.75rem',maxWidth:240}}
                  onClick={() => setActiveTab('subscription')}>{bt('biz_upgrade')}</button>
              )}
            </div>

            {/* Referral */}
            {ref.enabled !== false && (
              <div className="bd-card" style={{padding:'1.5rem',borderRadius:16}}>
                <h2 style={{margin:'0 0 .35rem',fontSize:'1.1rem'}}>{bt('biz_ref_title')}</h2>
                <p style={{margin:'0 0 1rem',opacity:.7,fontSize:'.9rem'}}>
                  {bt('biz_ref_desc')
                    .replace('{reward}', ref.rewardDays ?? 30)
                    .replace('{friend}', ref.friendRewardDays ?? 14)}
                </p>
                <div style={{display:'flex',gap:'.5rem',flexWrap:'wrap',alignItems:'center',marginBottom:'1rem'}}>
                  <input className="bd-input" readOnly value={ref.link || ''} style={{flex:'1 1 260px',minWidth:0}} />
                  <button className="bd-plan-btn primary" style={{maxWidth:160}} onClick={copyReferral}>
                    📋 {bt('biz_ref_copy')}
                  </button>
                </div>
                <div style={{display:'flex',gap:'2rem',flexWrap:'wrap'}}>
                  <div><div style={{fontSize:'1.6rem',fontWeight:800}}>{ref.referralCount ?? 0}</div>
                    <div style={{fontSize:'.8rem',opacity:.65}}>{bt('biz_ref_friends')}</div></div>
                  <div><div style={{fontSize:'1.6rem',fontWeight:800}}>{ref.bonusDays ?? 0}</div>
                    <div style={{fontSize:'.8rem',opacity:.65}}>{bt('biz_ref_bonus_days')}</div></div>
                  <div><div style={{fontSize:'1.6rem',fontWeight:800,letterSpacing:'.05em'}}>{ref.code || '—'}</div>
                    <div style={{fontSize:'.8rem',opacity:.65}}>{bt('biz_ref_code')}</div></div>
                </div>
              </div>
            )}
          </div>
          );
        })()}

        {activeTab === 'subscription' && (
          <div className="bd-page">
            <div className="bd-page-header">
              <div>
                <h1 className="bd-page-title">{bt('biz_sub_title')}</h1>
                <p className="bd-page-sub">{bt('biz_sub_sub')}</p>
              </div>
            </div>
            <div className="bd-plans">
              {(plans || DEFAULT_PLAN_CARDS).filter(p => p.active !== false).map(p => (
                <div key={p.id || p.name} className={`bd-plan-card ${p.highlight ? 'highlight' : ''}`}>
                  {p.highlight && <div className="bd-plan-badge">Popular</div>}
                  {businessOwner.plan === p.name && <div className="bd-plan-current">✓ {bt('biz_current_plan')}</div>}
                  <div className="bd-plan-name">{p.name}</div>
                  <div className="bd-plan-price">{p.currency === 'USD' || !p.currency ? '$' : ''}{p.price}<span>/{p.interval || 'mo'}</span></div>
                  {p.tagline && <div className="bd-plan-tagline">{p.tagline}</div>}
                  <ul className="bd-plan-features">
                    {planFeatureList(p).map(f => <li key={f}>{f}</li>)}
                  </ul>
                  <button className={`bd-plan-btn ${p.highlight ? 'primary' : ''}`}
                    onClick={() => alert(bt('biz_t_contact_sales'))}>
                    {businessOwner.plan === p.name ? bt('biz_current_plan') : bt('biz_upgrade')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── PAYOUT ── */}
        {activeTab === 'payout' && (() => {
          const method = payout.country ? methodForCountry(payout.country) : '';
          const fields = PAYOUT_FIELDS[method] || [];
          const setF = k => e => setPayout(p => ({ ...p, [k]: e.target.value }));
          return (
            <div className="bd-content">
              <div className="bd-page-head">
                <div>
                  <h1 className="bd-page-title">{bt('biz_payout_title')}</h1>
                  <p className="bd-page-sub">{bt('biz_payout_sub')}</p>
                </div>
              </div>
              <div className="bd-payout-banner">{bt('biz_payout_soon')}</div>
              <div className="bd-card bd-payout-card">
                <label className="bd-field-label">{bt('biz_payout_country')}</label>
                <select className="bd-input" value={payout.country || ''} onChange={setF('country')}>
                  <option value="">—</option>
                  {PAYOUT_COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
                </select>
                {fields.map(f => (
                  <div className="bd-field" key={f.key}>
                    <label className="bd-field-label">{f.label}</label>
                    <input className="bd-input" value={payout[f.key] || ''} onChange={setF(f.key)}
                      placeholder={f.placeholder} dir={f.ltr ? 'ltr' : undefined}
                      style={f.ltr ? { textAlign: 'left' } : undefined} />
                  </div>
                ))}
                {method && (
                  <button className="bd-btn primary" onClick={savePayout} disabled={saving}>
                    {saving ? '…' : bt('biz_payout_save')}
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── ACCOUNT ── */}
        {activeTab === 'account' && (
          <div className="bd-page">
            <div className="bd-page-header">
              <h1 className="bd-page-title">{bt('biz_account_title')}</h1>
            </div>
            <div className="bd-account-grid">
              <div className="bd-account-card">
                <h3>{bt('biz_business_info')}</h3>
                <label className="bd-field-label">{bt('biz_owner_name')}</label>
                <input className="bd-input" value={acct.ownerName} onChange={setA('ownerName')} />
                <label className="bd-field-label">{bt('biz_email')}</label>
                <input className="bd-input" defaultValue={businessOwner.email} disabled />
                <label className="bd-field-label">{bt('biz_phone')}</label>
                <input className="bd-input" value={acct.phone} onChange={setA('phone')} />
                <button className="bd-save-btn" onClick={saveAccount}>
                  {bt('biz_save')}
                </button>
              </div>
              <div className="bd-account-card">
                <h3>{bt('biz_change_pass')}</h3>
                <label className="bd-field-label">{bt('biz_current_pass')}</label>
                <input type="password" className="bd-input" value={pass.current} onChange={setP('current')} />
                <label className="bd-field-label">{bt('biz_new_pass')}</label>
                <input type="password" className="bd-input" value={pass.next} onChange={setP('next')} />
                <label className="bd-field-label">{bt('biz_confirm_pass')}</label>
                <input type="password" className="bd-input" value={pass.confirm} onChange={setP('confirm')} />
                <button className="bd-save-btn" onClick={savePassword}>
                  {bt('biz_update_pass')}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'language' && (
          <div className="bd-page">
            <div className="bd-page-header">
              <h1 className="bd-page-title">🌐 {bt('biz_lang_title')}</h1>
            </div>
            <div className="bd-account-card" style={{ maxWidth: 560 }}>
              <p style={{ color: 'rgba(255,255,255,.5)', fontSize: '.85rem', lineHeight: 1.6, marginBottom: '1.25rem' }}>
                {bt('biz_lang_sub')}
              </p>
              <label className="bd-field-label">{bt('biz_lang_title')}</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem', marginBottom: '1rem' }}>
                {Object.values(LANGUAGES).map(l => (
                  <button key={l.code}
                    onClick={() => !dashBusy && saveDashboardLanguage(l.code)}
                    disabled={dashBusy}
                    style={{
                      padding: '.55rem .9rem', borderRadius: 10, cursor: dashBusy ? 'wait' : 'pointer',
                      fontFamily: "'DM Sans',sans-serif", fontSize: '.85rem',
                      border: `1px solid ${dashLang === l.code ? '#c8a96e' : 'rgba(255,255,255,.12)'}`,
                      background: dashLang === l.code ? 'rgba(200,169,110,.18)' : 'rgba(255,255,255,.04)',
                      color: dashLang === l.code ? '#e8d5a8' : 'rgba(255,255,255,.6)', fontWeight: dashLang === l.code ? 700 : 500,
                    }}>
                    {l.flag} {l.name}
                  </button>
                ))}
              </div>
              {dashBusy && <div style={{ color: 'rgba(255,255,255,.45)', fontSize: '.82rem' }}>⏳ {bt('biz_lang_saving')}</div>}
              <p style={{ color: 'rgba(255,255,255,.3)', fontSize: '.75rem', marginTop: '.75rem', lineHeight: 1.5 }}>
                Selecting a language auto-translates the dashboard (free API, with AI fallback) and saves it to your account.
              </p>
            </div>
          </div>
        )}
      </main>

      {/* ── MODALS ── */}
      {addRestModal && (
        <AddRestaurantModal
          ownerEmail={businessOwner.email}
          ownerName={businessOwner.ownerName}
          saving={saving}
          onSave={handleAddRestaurant}
          onClose={() => setAddRestModal(false)}
        />
      )}
      {editRestModal && (
        <EditRestaurantModal
          restaurant={editRestModal.restaurant}
          saving={saving}
          onSave={(form) => handleEditRestaurant(editRestModal.restaurant._id, form)}
          onClose={() => setEditRestModal(null)}
        />
      )}
      {adminCredModal && (
        <AdminCredentialsModal
          restaurant={adminCredModal.restaurant}
          saving={saving}
          onSave={(form) => handleUpdateAdminCreds(adminCredModal.restaurant._id, form)}
          onClose={() => setAdminCredModal(null)}
        />
      )}
      {branchModal && (
        <BranchManagerModal
          restaurant={branchModal.restaurant}
          onClose={() => { setBranchModal(null); refreshRestaurants(); }}
          showToast={showToast}
        />
      )}

      <style>{BD_STYLES}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Restaurant Card
// ─────────────────────────────────────────────────────────────────────────────
function RestaurantCard({ restaurant: rest, onManageBranches, onEdit, onDelete, onAdminCreds, onRefresh }) {
  const navigate = useNavigate();
  const { bt } = useBusinessT();
  const branchCount = (rest.branches || []).filter(b => b.isActive).length;

  const goToAdmin = () => {
    // Navigate to the subdomain's admin panel
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost');
    if (isLocal) {
      window.location.href = `http://${rest.slug}.localhost:3000/admin`;
    } else {
      window.location.href = `https://${rest.slug}.nowshin.ir/admin`;
    }
  };

  const goToMenu = () => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost');
    if (isLocal) {
      window.open(`http://${rest.slug}.localhost:3000/`, '_blank');
    } else {
      window.open(`https://${rest.slug}.nowshin.ir/`, '_blank');
    }
  };

  return (
    <div className="bd-rest-card">
      {/* Card header accent */}
      <div className="bd-rest-card-accent" style={{ background: rest.primaryColor || '#c8a96e' }} />

      <div className="bd-rest-card-body">
        <div className="bd-rest-card-top">
          <div className="bd-rest-avatar" style={{ background: rest.primaryColor || '#c8a96e' }}>
            {rest.name?.[0]?.toUpperCase() || '🍽'}
          </div>
          <div className="bd-rest-badge-row">
            <span className={`bd-rest-status ${rest.isActive ? 'active' : 'inactive'}`}>
              {rest.isActive ? '● Live' : '○ Inactive'}
            </span>
          </div>
        </div>

        <h3 className="bd-rest-name">{rest.name}</h3>
        {rest.cuisine && <p className="bd-rest-cuisine">{rest.cuisine}</p>}

        <div className="bd-rest-url">
          <span className="bd-url-text">{rest.slug}.nowshin.ir</span>
        </div>

        <div className="bd-rest-meta">
          <span className="bd-rest-meta-item">
            🍽️ {rest.products?.length ?? 0} products
          </span>
          <span className="bd-rest-meta-item">
            📂 {rest.categories?.length ?? 0} categories
          </span>
        </div>

        {/* Branches strip */}
        <div className="bd-branches-strip">
          <div className="bd-branches-strip-header">
            <span className="bd-branches-label">
              {branchCount === 0
                ? '1 Location (main)'
                : `1 main + ${branchCount} branch${branchCount > 1 ? 'es' : ''}`}
            </span>
            <button className="bd-manage-branches-btn" onClick={onManageBranches}>
              Manage Branches →
            </button>
          </div>
          <div className="bd-branch-pills">
            <span className="bd-branch-pill main">🏠 Main</span>
            {(rest.branches || []).filter(b => b.isActive).slice(0, 3).map(b => (
              <span key={b._id || b.slug} className="bd-branch-pill">{b.name}</span>
            ))}
            {(rest.branches || []).filter(b => b.isActive).length > 3 && (
              <span className="bd-branch-pill more">+{(rest.branches || []).filter(b => b.isActive).length - 3}</span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="bd-rest-actions">
          <button className="bd-action-btn primary" onClick={goToAdmin}>
            ⚙️ {bt('biz_open_admin')}
          </button>
          <button className="bd-action-btn" onClick={goToMenu}>
            🌐 {bt('biz_view_site')}
          </button>
        </div>
        {/* Owner management row */}
        <div className="bd-rest-owner-actions">
          <button className="bd-owner-action-btn" onClick={onEdit} title="Edit restaurant info">
            ✏️ {bt('biz_edit_info')}
          </button>
          <button className="bd-owner-action-btn" onClick={onAdminCreds} title="Change admin login credentials">
            🔐 {bt('biz_admin_creds')}
          </button>
          <button className="bd-owner-action-btn danger" onClick={onDelete} title="Delete restaurant permanently">
            🗑️ {bt('biz_delete')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Add Restaurant Modal
// ─────────────────────────────────────────────────────────────────────────────
function AddRestaurantModal({ ownerEmail, ownerName, saving, onSave, onClose }) {
  const { bt } = useBusinessT();
  const [step, setStep] = useState(1); // 1 = basics, 2 = details, 3 = admin
  const [form, setForm] = useState({
    restaurantName: '',
    restaurantSlug: '',
    cuisine: '',
    tagline: '',
    address: '',
    restaurantPhone: '',
    restaurantEmail: ownerEmail || '',
    hours: '',
    adminEmail: ownerEmail || '',
    adminPassword: '',
    primaryColor: '#c8a96e',
  });
  const [slugEdited, setSlugEdited] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleNameChange = (v) => {
    set('restaurantName', v);
    if (!slugEdited) set('restaurantSlug', slugify(v));
  };

  const validate1 = () => {
    const e = {};
    if (!form.restaurantName.trim()) e.restaurantName = 'Name is required';
    if (!form.restaurantSlug.trim()) e.restaurantSlug = 'Subdomain is required';
    if (!/^[a-z0-9-]+$/.test(form.restaurantSlug)) e.restaurantSlug = 'Only lowercase letters, numbers and hyphens';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!form.adminEmail || !form.adminPassword) {
      setErrors({ adminPassword: 'Admin credentials are required' });
      return;
    }
    onSave(form);
  };

  return (
    <div className="bd-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bd-modal">
        {/* Header */}
        <div className="bd-modal-header">
          <div className="bd-modal-title-area">
            <span className="bd-modal-icon">🏪</span>
            <div>
              <h2>{bt("biz_new_restaurant")}</h2>
              <p>Step {step} of 3 — {['Basics','Details','Admin Account'][step-1]}</p>
            </div>
          </div>
          <button className="bd-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Step indicator */}
        <div className="bd-steps">
          {['Basics','Details','Admin'].map((s, i) => (
            <div key={s} className={`bd-step ${step > i ? 'done' : ''} ${step === i+1 ? 'active' : ''}`}>
              <div className="bd-step-dot">{step > i+1 ? '✓' : i+1}</div>
              <span>{s}</span>
            </div>
          ))}
          <div className="bd-steps-line" />
        </div>

        {/* Step 1: Basics */}
        {step === 1 && (
          <div className="bd-modal-body">
            <div className="bd-field">
              <label>Restaurant / Café Name <span className="req">*</span></label>
              <input className={`bd-input ${errors.restaurantName ? 'error' : ''}`}
                placeholder="e.g. Yasmin Café"
                value={form.restaurantName}
                onChange={e => handleNameChange(e.target.value)} />
              {errors.restaurantName && <span className="bd-err">{errors.restaurantName}</span>}
            </div>

            <div className="bd-field">
              <label>{bt("biz_subdomain_url")} <span className="req">*</span></label>
              <div className="bd-slug-row">
                <input className={`bd-input bd-slug-input ${errors.restaurantSlug ? 'error' : ''}`}
                  placeholder="yasmin-cafe"
                  value={form.restaurantSlug}
                  onChange={e => { setSlugEdited(true); set('restaurantSlug', slugify(e.target.value)); }} />
                <span className="bd-slug-suffix">.nowshin.ir</span>
              </div>
              {errors.restaurantSlug && <span className="bd-err">{errors.restaurantSlug}</span>}
              {form.restaurantSlug && !errors.restaurantSlug && (
                <span className="bd-slug-preview">
                  🌐 Your site: <strong>{form.restaurantSlug}.nowshin.ir</strong>
                </span>
              )}
            </div>

            <div className="bd-field">
              <label>{bt("biz_cuisine_type")}</label>
              <input className="bd-input" placeholder="e.g. Persian, Italian, Café…"
                value={form.cuisine} onChange={e => set('cuisine', e.target.value)} />
            </div>

            <div className="bd-field">
              <label>{bt("biz_brand_color")}</label>
              <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center' }}>
                <input type="color" value={form.primaryColor}
                  onChange={e => set('primaryColor', e.target.value)}
                  style={{ width: 48, height: 40, border: 'none', cursor: 'pointer', borderRadius: 8 }} />
                <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.85rem' }}>{form.primaryColor}</span>
                <div style={{ display: 'flex', gap: '.4rem' }}>
                  {['#c8a96e','#6366f1','#ec4899','#10b981','#f97316'].map(c => (
                    <button key={c} onClick={() => set('primaryColor', c)}
                      style={{ width: 24, height: 24, borderRadius: '50%', background: c, border: form.primaryColor === c ? '2px solid #fff' : '2px solid transparent', cursor: 'pointer' }} />
                  ))}
                </div>
              </div>
            </div>

            <div className="bd-modal-actions">
              <button className="bd-modal-cancel" onClick={onClose}>{bt("biz_cancel")}</button>
              <button className="bd-modal-next" onClick={() => { if (validate1()) setStep(2); }}>
                Next: Details →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div className="bd-modal-body">
            <div className="bd-field">
              <label>{bt("biz_tagline")}</label>
              <input className="bd-input" placeholder="e.g. Authentic Persian flavors since 1994"
                value={form.tagline} onChange={e => set('tagline', e.target.value)} />
            </div>
            <div className="bd-field">
              <label>{bt("biz_address")}</label>
              <input className="bd-input" placeholder="Street, City, Country"
                value={form.address} onChange={e => set('address', e.target.value)} />
            </div>
            <div className="bd-field-row">
              <div className="bd-field">
                <label>{bt("biz_phone")}</label>
                <input className="bd-input" placeholder="+1 555 000 0000"
                  value={form.restaurantPhone} onChange={e => set('restaurantPhone', e.target.value)} />
              </div>
              <div className="bd-field">
                <label>{bt("biz_email")}</label>
                <input className="bd-input" placeholder="hello@restaurant.com"
                  value={form.restaurantEmail} onChange={e => set('restaurantEmail', e.target.value)} />
              </div>
            </div>
            <div className="bd-field">
              <label>{bt("biz_hours")}</label>
              <input className="bd-input" placeholder="e.g. Mon–Fri 9am–10pm"
                value={form.hours} onChange={e => set('hours', e.target.value)} />
            </div>
            <div className="bd-modal-actions">
              <button className="bd-modal-cancel" onClick={() => setStep(1)}>← Back</button>
              <button className="bd-modal-next" onClick={() => setStep(3)}>Next: Admin →</button>
            </div>
          </div>
        )}

        {/* Step 3: Admin Account */}
        {step === 3 && (
          <div className="bd-modal-body">
            <div className="bd-admin-info-banner">
              <span>🔐</span>
              <p>These credentials will be used to log in to the <strong>{form.restaurantSlug || 'new restaurant'}</strong> admin panel at <strong>{form.restaurantSlug}.nowshin.ir/admin</strong></p>
            </div>
            <div className="bd-field">
              <label>{bt("biz_admin_email")} <span className="req">*</span></label>
              <input className={`bd-input ${errors.adminEmail ? 'error' : ''}`}
                value={form.adminEmail} onChange={e => { set('adminEmail', e.target.value); setErrors({}); }} />
            </div>
            <div className="bd-field">
              <label>{bt("biz_admin_password")} <span className="req">*</span></label>
              <input type="password" className={`bd-input ${errors.adminPassword ? 'error' : ''}`}
                placeholder={bt("biz_min6")}
                value={form.adminPassword} onChange={e => { set('adminPassword', e.target.value); setErrors({}); }} />
              {errors.adminPassword && <span className="bd-err">{errors.adminPassword}</span>}
            </div>

            {/* Summary */}
            <div className="bd-summary">
              <div className="bd-summary-title">{bt("biz_summary")}</div>
              <div className="bd-summary-row"><span>Name</span><strong>{form.restaurantName}</strong></div>
              <div className="bd-summary-row"><span>URL</span><strong>{form.restaurantSlug}.nowshin.ir</strong></div>
              {form.cuisine && <div className="bd-summary-row"><span>Cuisine</span><strong>{form.cuisine}</strong></div>}
            </div>

            <div className="bd-modal-actions">
              <button className="bd-modal-cancel" onClick={() => setStep(2)}>← Back</button>
              <button className="bd-modal-save" onClick={handleSubmit} disabled={saving}>
                {saving ? '⏳ Creating…' : '🏪 Create Restaurant'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Branch Manager Modal
// ─────────────────────────────────────────────────────────────────────────────
function BranchManagerModal({ restaurant: initRest, onClose, showToast }) {
  const { bt } = useBusinessT();
  const [restaurant, setRestaurant] = useState(initRest);
  const [branches, setBranches]     = useState(initRest.branches || []);
  const [loading,  setLoading]      = useState(true);
  const [adding,   setAdding]       = useState(false);
  const [editId,   setEditId]       = useState(null);
  const [saving,   setSaving]       = useState(false);
  const [newForm,  setNewForm]       = useState({ name: '', slug: '', address: '', phone: '', hours: '' });
  const [editForm, setEditForm]      = useState({});
  const [slugEdited, setSlugEdited] = useState(false);
  const [errors,   setErrors]       = useState({});

  // Always fetch fresh branch list from the API when the modal opens
  useEffect(() => {
    let cancelled = false;
    const fetchBranches = async () => {
      setLoading(true);
      try {
        const r = await businessApi.getBranches(initRest._id);
        if (!cancelled) setBranches(r.data || []);
      } catch {
        if (!cancelled) setBranches(initRest.branches || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBranches();
    return () => { cancelled = true; };
  }, [initRest._id]);

  const setN = (k, v) => setNewForm(f => ({ ...f, [k]: v }));
  const setE = (k, v) => setEditForm(f => ({ ...f, [k]: v }));

  const handleNewName = (v) => {
    setN('name', v);
    if (!slugEdited) setN('slug', slugify(v));
  };

  const handleAddBranch = async () => {
    const e = {};
    if (!newForm.name.trim()) e.name = 'Branch name required';
    if (!newForm.slug.trim()) e.slug = 'Branch slug required';
    if (!/^[a-z0-9-]+$/.test(newForm.slug)) e.slug = 'Only lowercase letters, numbers, hyphens';
    if (branches.some(b => b.slug === newForm.slug.toLowerCase())) e.slug = 'This slug already exists';
    if (Object.keys(e).length) { setErrors(e); return; }

    setSaving(true);
    try {
      const r = await businessApi.addBranch(restaurant._id, {
        name: newForm.name.trim(),
        slug: newForm.slug.toLowerCase(),
        address: newForm.address,
        phone: newForm.phone,
        hours: newForm.hours,
      });
      setBranches(prev => [...prev, r.data]);
      setNewForm({ name: '', slug: '', address: '', phone: '', hours: '' });
      setSlugEdited(false);
      setAdding(false);
      setErrors({});
      showToast(`"${r.data.name}" `+bt('biz_t_branch_created'));
    } catch (err) {
      showToast(err.message || bt('biz_t_create_failed'), 'error');
    } finally { setSaving(false); }
  };

  const handleSaveEdit = async (branchId) => {
    setSaving(true);
    try {
      const r = await businessApi.updateBranch(restaurant._id, branchId, editForm);
      setBranches(prev => prev.map(b => b._id === branchId ? r.data : b));
      setEditId(null);
      showToast(bt('biz_t_branch_updated'));
    } catch (err) {
      showToast(err.message || bt('biz_t_update_failed'), 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (branchId, branchName) => {
    if (!window.confirm(`Delete branch "${branchName}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await businessApi.deleteBranch(restaurant._id, branchId);
      setBranches(prev => prev.filter(b => b._id !== branchId));
      showToast(bt('biz_t_branch_deleted'));
    } catch (err) {
      showToast(err.message || bt('biz_t_update_failed'), 'error');
    } finally { setSaving(false); }
  };

  const toggleActive = async (branch) => {
    try {
      const r = await businessApi.updateBranch(restaurant._id, branch._id, { isActive: !branch.isActive });
      setBranches(prev => prev.map(b => b._id === branch._id ? r.data : b));
    } catch { showToast(bt('biz_t_toggle_failed'), 'error'); }
  };

  return (
    <div className="bd-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bd-modal wide">
        <div className="bd-modal-header">
          <div className="bd-modal-title-area">
            <span className="bd-modal-icon">🌿</span>
            <div>
              <h2>Branches — {restaurant.name}</h2>
              <p>Each branch has its own menu and shows as a separate "Enter Menu" button on your landing page</p>
            </div>
          </div>
          <button className="bd-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="bd-modal-body">
          {/* How it works info */}
          {loading && (
            <div style={{textAlign:'center',padding:'2rem',color:'rgba(255,255,255,.4)',fontSize:'.9rem'}}>
              ⏳ Loading branches…
            </div>
          )}
          {!loading && (<><div className="bd-branch-how">
            <div className="bd-branch-how-item">
              <span>🌐</span>
              <div>
                <strong>{bt("biz_same_subdomain")}</strong>
                <span>All branches live at <em>{restaurant.slug}.nowshin.ir</em></span>
              </div>
            </div>
            <div className="bd-branch-how-arrow">→</div>
            <div className="bd-branch-how-item">
              <span>🔀</span>
              <div>
                <strong>{bt("biz_branch_selector")}</strong>
                <span>{bt("biz_branch_sel_desc")}</span>
              </div>
            </div>
            <div className="bd-branch-how-arrow">→</div>
            <div className="bd-branch-how-item">
              <span>🍽️</span>
              <div>
                <strong>{bt("biz_own_menu")}</strong>
                <span>Each branch has its own products &amp; categories</span>
              </div>
            </div>
          </div>

          {/* Default branch (main restaurant) */}
          <div className="bd-branch-list">
            <div className="bd-branch-item main">
              <div className="bd-branch-item-left">
                <span className="bd-branch-main-badge">{bt("biz_main")}</span>
                <div>
                  <div className="bd-branch-name">{restaurant.name}</div>
                  <div className="bd-branch-url">{restaurant.slug}.nowshin.ir/menu</div>
                  {restaurant.address && <div className="bd-branch-addr">📍 {restaurant.address}</div>}
                </div>
              </div>
              <div className="bd-branch-item-right">
                <span className="bd-branch-status active">● Always active</span>
                <a className="bd-branch-link"
                  href={`http://${restaurant.slug}.localhost:3000/menu`}
                  target="_blank" rel="noreferrer">
                  Open Menu ↗
                </a>
              </div>
            </div>

            {/* Extra branches */}
            {branches.map(branch => (
              <div key={branch._id || branch.slug} className={`bd-branch-item ${!branch.isActive ? 'inactive' : ''}`}>
                {editId === branch._id ? (
                  // ── Edit mode ──
                  <div className="bd-branch-edit-form">
                    <div className="bd-field-row">
                      <div className="bd-field">
                        <label>{bt("biz_branch_name")}</label>
                        <input className="bd-input sm"
                          value={editForm.name ?? branch.name}
                          onChange={e => setE('name', e.target.value)} />
                      </div>
                      <div className="bd-field">
                        <label>{bt("biz_address")}</label>
                        <input className="bd-input sm"
                          value={editForm.address ?? branch.address ?? ''}
                          onChange={e => setE('address', e.target.value)} />
                      </div>
                    </div>
                    <div className="bd-field-row">
                      <div className="bd-field">
                        <label>{bt("biz_phone")}</label>
                        <input className="bd-input sm"
                          value={editForm.phone ?? branch.phone ?? ''}
                          onChange={e => setE('phone', e.target.value)} />
                      </div>
                      <div className="bd-field">
                        <label>{bt("biz_hours")}</label>
                        <input className="bd-input sm"
                          value={editForm.hours ?? branch.hours ?? ''}
                          onChange={e => setE('hours', e.target.value)} />
                      </div>
                    </div>
                    <div className="bd-branch-edit-actions">
                      <button className="bd-branch-save-btn" onClick={() => handleSaveEdit(branch._id)} disabled={saving}>
                        {saving ? '⏳' : '✓ Save'}
                      </button>
                      <button className="bd-branch-cancel-btn" onClick={() => { setEditId(null); setEditForm({}); }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // ── View mode ──
                  <>
                    <div className="bd-branch-item-left">
                      <div className="bd-branch-order-dot" />
                      <div>
                        <div className="bd-branch-name">{branch.name}</div>
                        <div className="bd-branch-url">{restaurant.slug}.nowshin.ir/menu?branch={branch.slug}</div>
                        {branch.address && <div className="bd-branch-addr">📍 {branch.address}</div>}
                        {branch.hours   && <div className="bd-branch-addr">🕐 {branch.hours}</div>}
                      </div>
                    </div>
                    <div className="bd-branch-item-right">
                      <button className={`bd-branch-toggle ${branch.isActive ? 'on' : 'off'}`}
                        onClick={() => toggleActive(branch)}>
                        {branch.isActive ? '● Active' : '○ Hidden'}
                      </button>
                      <button className="bd-branch-edit-btn" onClick={() => {
                        setEditId(branch._id);
                        setEditForm({ name: branch.name, address: branch.address || '', phone: branch.phone || '', hours: branch.hours || '' });
                      }}>{bt("biz_edit")}</button>
                      <button className="bd-branch-del-btn" onClick={() => handleDelete(branch._id, branch.name)}>✕</button>
                    </div>
                  </>
                )}
              </div>
            ))}

            {/* Add branch form */}
            {adding ? (
              <div className="bd-branch-add-form">
                <div className="bd-branch-add-form-title">➕ New Branch</div>
                <div className="bd-field-row">
                  <div className="bd-field">
                    <label>{bt("biz_branch_name")} <span className="req">*</span></label>
                    <input className={`bd-input sm ${errors.name ? 'error' : ''}`}
                      placeholder="e.g. North Branch"
                      value={newForm.name}
                      onChange={e => handleNewName(e.target.value)} />
                    {errors.name && <span className="bd-err">{errors.name}</span>}
                  </div>
                  <div className="bd-field">
                    <label>{bt("biz_branch_slug")} <span className="req">*</span></label>
                    <div className="bd-slug-row">
                      <input className={`bd-input sm bd-slug-input ${errors.slug ? 'error' : ''}`}
                        placeholder="north-branch"
                        value={newForm.slug}
                        onChange={e => { setSlugEdited(true); setN('slug', slugify(e.target.value)); }} />
                      <span className="bd-slug-suffix sm">?branch=…</span>
                    </div>
                    {errors.slug && <span className="bd-err">{errors.slug}</span>}
                  </div>
                </div>
                <div className="bd-field-row">
                  <div className="bd-field">
                    <label>{bt("biz_address")}</label>
                    <input className="bd-input sm" placeholder={bt("biz_branch_address")}
                      value={newForm.address} onChange={e => setN('address', e.target.value)} />
                  </div>
                  <div className="bd-field">
                    <label>{bt("biz_phone")}</label>
                    <input className="bd-input sm" placeholder={bt("biz_branch_phone")}
                      value={newForm.phone} onChange={e => setN('phone', e.target.value)} />
                  </div>
                </div>
                <div className="bd-field">
                  <label>{bt("biz_opening_hours")}</label>
                  <input className="bd-input sm" placeholder="Mon–Fri 9am–10pm"
                    value={newForm.hours} onChange={e => setN('hours', e.target.value)} />
                </div>
                <div className="bd-branch-edit-actions">
                  <button className="bd-branch-save-btn" onClick={handleAddBranch} disabled={saving}>
                    {saving ? '⏳ Creating…' : '✓ Add Branch'}
                  </button>
                  <button className="bd-branch-cancel-btn" onClick={() => { setAdding(false); setErrors({}); }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button className="bd-add-branch-btn" onClick={() => setAdding(true)}>
                + Add New Branch
              </button>
            )}
          </div>

          {/* Landing page preview */}
          <div className="bd-landing-preview">
            <div className="bd-landing-preview-title">{bt("biz_landing_preview")}</div>
            <p className="bd-landing-preview-sub">This is how your "Enter Menu" buttons will look on <em>{restaurant.slug}.nowshin.ir</em></p>
            <div className="bd-landing-preview-buttons">
              {/* Main restaurant button */}
              <div className="bd-preview-btn" style={{ borderColor: restaurant.primaryColor || '#c8a96e' }}>
                <span>{bt("biz_enter_menu")}</span>
                <span className="bd-preview-btn-arrow" style={{ color: restaurant.primaryColor || '#c8a96e' }}>→</span>
              </div>
              {/* Branch buttons */}
              {branches.filter(b => b.isActive).map(b => (
                <div key={b._id} className="bd-preview-btn branch" style={{ borderColor: restaurant.primaryColor || '#c8a96e' }}>
                  <div>
                    <div className="bd-preview-btn-top">{bt("biz_enter_menu")}</div>
                    <div className="bd-preview-btn-branch" style={{ color: restaurant.primaryColor || '#c8a96e' }}>{b.name}</div>
                  </div>
                  <span className="bd-preview-btn-arrow" style={{ color: restaurant.primaryColor || '#c8a96e' }}>→</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bd-modal-actions" style={{ marginTop: '1.5rem' }}>
            <button className="bd-modal-save" onClick={onClose} style={{ maxWidth: 200, marginLeft: 'auto' }}>
              Done ✓
            </button>
          </div></>)}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Edit Restaurant Modal
// ─────────────────────────────────────────────────────────────────────────────
function EditRestaurantModal({ restaurant: rest, saving, onSave, onClose }) {
  const { bt } = useBusinessT();
  const [form, setForm] = useState({
    name: rest.name || '',
    tagline: rest.tagline || '',
    cuisine: rest.cuisine || '',
    address: rest.address || '',
    phone: rest.phone || '',
    email: rest.email || '',
    hours: rest.hours || '',
    primaryColor: rest.primaryColor || '#c8a96e',
    isActive: rest.isActive !== false,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="bd-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bd-modal">
        <div className="bd-modal-header">
          <div className="bd-modal-title-area">
            <span className="bd-modal-icon">✏️</span>
            <div>
              <h2>{bt("biz_edit_restaurant")}</h2>
              <p>{rest.slug}.nowshin.ir</p>
            </div>
          </div>
          <button className="bd-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="bd-modal-body">
          <div className="bd-field">
            <label>{bt("biz_restaurant_name")}</label>
            <input className="bd-input" value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div className="bd-field">
            <label>{bt("biz_tagline")}</label>
            <input className="bd-input" value={form.tagline} onChange={e => set('tagline', e.target.value)} />
          </div>
          <div className="bd-field-row">
            <div className="bd-field">
              <label>{bt("biz_cuisine")}</label>
              <input className="bd-input" value={form.cuisine} onChange={e => set('cuisine', e.target.value)} />
            </div>
            <div className="bd-field">
              <label>{bt("biz_phone")}</label>
              <input className="bd-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
          </div>
          <div className="bd-field">
            <label>{bt("biz_address")}</label>
            <input className="bd-input" value={form.address} onChange={e => set('address', e.target.value)} />
          </div>
          <div className="bd-field-row">
            <div className="bd-field">
              <label>{bt("biz_email")}</label>
              <input className="bd-input" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div className="bd-field">
              <label>{bt("biz_hours")}</label>
              <input className="bd-input" value={form.hours} onChange={e => set('hours', e.target.value)} />
            </div>
          </div>
          <div className="bd-field">
            <label>{bt("biz_brand_color")}</label>
            <div style={{display:'flex',gap:'.75rem',alignItems:'center'}}>
              <input type="color" value={form.primaryColor} onChange={e => set('primaryColor', e.target.value)}
                style={{width:48,height:40,border:'none',cursor:'pointer',borderRadius:8}} />
              <span style={{color:'rgba(255,255,255,.4)',fontSize:'.85rem'}}>{form.primaryColor}</span>
              {['#c8a96e','#6366f1','#ec4899','#10b981','#f97316'].map(c => (
                <button key={c} onClick={() => set('primaryColor', c)}
                  style={{width:24,height:24,borderRadius:'50%',background:c,border:form.primaryColor===c?'2px solid #fff':'2px solid transparent',cursor:'pointer'}} />
              ))}
            </div>
          </div>
          <div className="bd-field">
            <label>{bt("biz_status")}</label>
            <div style={{display:'flex',gap:'.75rem'}}>
              {[{v:true,l:'🟢 Active (visible to customers)'},{v:false,l:'⚫ Inactive (hidden)'}].map(o => (
                <button key={String(o.v)} onClick={() => set('isActive', o.v)}
                  className={`bd-status-toggle ${form.isActive === o.v ? 'active' : ''}`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div className="bd-modal-actions">
            <button className="bd-modal-cancel" onClick={onClose}>{bt("biz_cancel")}</button>
            <button className="bd-modal-save" onClick={() => onSave(form)} disabled={saving}>
              {saving ? '⏳ Saving…' : '✓ Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin Credentials Modal
// ─────────────────────────────────────────────────────────────────────────────
function AdminCredentialsModal({ restaurant: rest, saving, onSave, onClose }) {
  const { bt } = useBusinessT();
  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors({}); };

  const handleSave = () => {
    const e = {};
    if (!form.email && !form.password) { e.general = 'Enter a new email, password, or both.'; }
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) { e.email = 'Enter a valid email address.'; }
    if (form.password && form.password.length < 6) { e.password = 'Password must be at least 6 characters.'; }
    if (Object.keys(e).length) { setErrors(e); return; }
    const payload = {};
    if (form.email.trim()) payload.email = form.email.trim();
    if (form.password) payload.password = form.password;
    onSave(payload);
  };

  return (
    <div className="bd-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bd-modal">
        <div className="bd-modal-header">
          <div className="bd-modal-title-area">
            <span className="bd-modal-icon">🔐</span>
            <div>
              <h2>{bt("biz_admin_login_creds")}</h2>
              <p>Change the login for <strong>{rest.name}</strong>'s admin panel</p>
            </div>
          </div>
          <button className="bd-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="bd-modal-body">
          <div className="bd-admin-info-banner">
            <span>ℹ️</span>
            <p>Leave a field blank to keep it unchanged. The admin uses these credentials at <strong>{rest.slug}.nowshin.ir/admin</strong></p>
          </div>
          {errors.general && <span className="bd-err">{errors.general}</span>}
          <div className="bd-field">
            <label>{bt("biz_new_admin_email")}</label>
            <input className={`bd-input ${errors.email ? 'error' : ''}`}
              type="email" placeholder={bt("biz_leave_blank_keep")}
              value={form.email} onChange={e => set('email', e.target.value)} />
            {errors.email && <span className="bd-err">{errors.email}</span>}
          </div>
          <div className="bd-field">
            <label>{bt("biz_new_admin_pass")}</label>
            <input className={`bd-input ${errors.password ? 'error' : ''}`}
              type="password" placeholder={bt("biz_leave_blank_keep")}
              value={form.password} onChange={e => set('password', e.target.value)} />
            {errors.password && <span className="bd-err">{errors.password}</span>}
          </div>
          <div className="bd-modal-actions">
            <button className="bd-modal-cancel" onClick={onClose}>{bt("biz_cancel")}</button>
            <button className="bd-modal-save" onClick={handleSave} disabled={saving}>
              {saving ? '⏳ Updating…' : '🔐 Update Credentials'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
const BD_STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  @import url('/fonts/fonts.css');

  :root {
    --gold: #c8a96e;
    --gold-dim: rgba(200,169,110,.12);
    --gold-glow: rgba(200,169,110,.25);
    --surface: rgba(255,255,255,.04);
    --border: rgba(255,255,255,.07);
    --text: #f0ebe0;
    --text-dim: rgba(240,235,224,.4);
    --text-mid: rgba(240,235,224,.65);
  }

  .bd-root { display: flex; min-height: 100vh; background: #090909; color: var(--text); font-family: 'DM Sans', sans-serif; }

  /* ── Toast ── */
  .bd-toast { position: fixed; top: 1.5rem; right: 1.5rem; z-index: 9999; padding: .9rem 1.6rem; border-radius: 12px; font-size: .88rem; font-weight: 600; animation: slideInRight .3s ease; box-shadow: 0 8px 32px rgba(0,0,0,.5); }
  .bd-toast.success { background: #0d1a14; border: 1px solid rgba(16,185,129,.3); color: #6ee7b7; }
  .bd-toast.error   { background: #1a0d0d; border: 1px solid rgba(239,68,68,.3); color: #fca5a5; }
  @keyframes slideInRight { from { transform: translateX(40px); opacity: 0; } to { transform: none; opacity: 1; } }

  /* ── Sidebar ── */
  .bd-sidebar { width: 240px; flex-shrink: 0; background: #0d0d0d; border-right: 1px solid var(--border); display: flex; flex-direction: column; padding: 1.5rem 1rem; gap: 0; position: sticky; top: 0; height: 100vh; overflow: hidden; }
  .bd-sidebar-logo { display: flex; align-items: center; gap: .6rem; padding: .25rem .5rem 1.75rem; font-family: 'Cormorant Garamond', serif; font-size: 1.4rem; font-weight: 600; color: #fff; }
  .bd-logo-star { color: var(--gold); font-size: 1.1rem; }
  .bd-sidebar-owner { display: flex; align-items: center; gap: .75rem; padding: 1rem; background: var(--surface); border: 1px solid var(--border); border-radius: 14px; margin-bottom: 1.5rem; }
  .bd-owner-avatar { width: 38px; height: 38px; border-radius: 50%; background: var(--gold); display: flex; align-items: center; justify-content: center; font-weight: 700; color: #fff; font-size: .95rem; flex-shrink: 0; }
  .bd-owner-name { font-size: .85rem; font-weight: 600; color: #fff; line-height: 1.3; }
  .bd-owner-plan { font-size: .7rem; color: var(--gold); text-transform: uppercase; letter-spacing: .07em; }
  .bd-nav { display: flex; flex-direction: column; gap: .2rem; flex: 1; }
  .bd-nav-btn { display: flex; align-items: center; gap: .75rem; padding: .7rem 1rem; border: none; background: none; color: var(--text-dim); cursor: pointer; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: .875rem; text-align: left; transition: all .2s; }
  .bd-nav-btn:hover { background: var(--surface); color: var(--text); }
  .bd-nav-btn.active { background: var(--gold-dim); color: var(--gold); font-weight: 600; border: 1px solid rgba(200,169,110,.2); }
  .bd-nav-icon { font-size: 1rem; width: 20px; text-align: center; }
  .bd-sidebar-logout { display: flex; align-items: center; gap: .6rem; padding: .6rem 1rem; background: none; border: 1px solid rgba(255,255,255,.08); border-radius: 10px; color: var(--text-dim); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .82rem; transition: all .2s; margin-top: 1rem; }
  .bd-sidebar-logout:hover { border-color: rgba(239,68,68,.3); color: #fca5a5; }

  /* ── Main area ── */
  .bd-main { flex: 1; overflow-y: auto; }
  .bd-page { max-width: 1100px; margin: 0 auto; padding: 2.5rem 2rem; }
  .bd-page-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 2rem; gap: 1rem; }
  .bd-page-title { font-family: 'Cormorant Garamond', serif; font-size: 2.25rem; font-weight: 600; color: #fff; line-height: 1.1; }
  .bd-page-sub { color: var(--text-dim); font-size: .875rem; margin-top: .3rem; }
  .bd-add-rest-btn { display: inline-flex; align-items: center; gap: .5rem; padding: .75rem 1.4rem; background: var(--gold); border: none; border-radius: 22px; color: #fff; font-family: 'DM Sans', sans-serif; font-size: .88rem; font-weight: 700; cursor: pointer; transition: opacity .2s; white-space: nowrap; box-shadow: 0 4px 16px var(--gold-glow); }
  .bd-add-rest-btn:hover { opacity: .88; }

  /* ── Empty state ── */
  .bd-empty { text-align: center; padding: 5rem 2rem; }
  .bd-empty-icon { font-size: 4rem; margin-bottom: 1rem; }
  .bd-empty h3 { font-family: 'Cormorant Garamond', serif; font-size: 1.5rem; color: #fff; margin-bottom: .5rem; }
  .bd-empty p { color: var(--text-dim); margin-bottom: 1.5rem; }

  /* ── Restaurant grid ── */
  .bd-rest-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 1.5rem; }

  /* ── Restaurant card ── */
  .bd-rest-card { background: #111; border: 1px solid var(--border); border-radius: 20px; overflow: hidden; transition: transform .2s, box-shadow .2s; }
  .bd-rest-card:hover { transform: translateY(-2px); box-shadow: 0 12px 40px rgba(0,0,0,.4); }
  .bd-rest-card-accent { height: 4px; }
  .bd-rest-card-body { padding: 1.5rem; }
  .bd-rest-card-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1rem; }
  .bd-rest-avatar { width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; font-weight: 700; color: #fff; }
  .bd-rest-badge-row { display: flex; gap: .4rem; }
  .bd-rest-status { font-size: .7rem; font-weight: 700; padding: .25rem .7rem; border-radius: 10px; }
  .bd-rest-status.active { background: rgba(16,185,129,.1); color: #6ee7b7; border: 1px solid rgba(16,185,129,.2); }
  .bd-rest-status.inactive { background: rgba(255,255,255,.05); color: var(--text-dim); border: 1px solid var(--border); }
  .bd-rest-name { font-family: 'Cormorant Garamond', serif; font-size: 1.4rem; font-weight: 600; color: #fff; margin-bottom: .2rem; }
  .bd-rest-cuisine { font-size: .8rem; color: var(--text-dim); margin-bottom: .75rem; }
  .bd-rest-url { background: rgba(255,255,255,.04); border: 1px solid var(--border); border-radius: 8px; padding: .45rem .85rem; margin-bottom: .75rem; }
  .bd-url-text { font-family: monospace; font-size: .8rem; color: var(--gold); }
  .bd-rest-meta { display: flex; gap: 1rem; margin-bottom: 1.1rem; }
  .bd-rest-meta-item { font-size: .78rem; color: var(--text-dim); }

  /* ── Branches strip on card ── */
  .bd-branches-strip { background: rgba(255,255,255,.03); border: 1px solid var(--border); border-radius: 10px; padding: .75rem 1rem; margin-bottom: 1.1rem; }
  .bd-branches-strip-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: .5rem; }
  .bd-branches-label { font-size: .75rem; color: var(--text-dim); }
  .bd-manage-branches-btn { font-size: .75rem; color: var(--gold); background: none; border: none; cursor: pointer; font-family: 'DM Sans', sans-serif; padding: 0; transition: opacity .2s; }
  .bd-manage-branches-btn:hover { opacity: .75; }
  .bd-branch-pills { display: flex; gap: .4rem; flex-wrap: wrap; }
  .bd-branch-pill { font-size: .68rem; padding: .18rem .55rem; border-radius: 8px; background: var(--surface); border: 1px solid var(--border); color: var(--text-mid); }
  .bd-branch-pill.main { background: var(--gold-dim); border-color: rgba(200,169,110,.25); color: var(--gold); }
  .bd-branch-pill.more { background: none; color: var(--text-dim); }

  /* ── Card actions ── */
  .bd-rest-actions { display: flex; gap: .6rem; }
  .bd-action-btn { flex: 1; padding: .65rem; border-radius: 10px; border: 1px solid var(--border); background: none; color: var(--text-mid); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .82rem; font-weight: 600; transition: all .2s; }
  .bd-action-btn:hover { background: var(--surface); color: #fff; }
  .bd-action-btn.primary { background: var(--gold-dim); border-color: rgba(200,169,110,.3); color: var(--gold); }
  .bd-action-btn.primary:hover { background: rgba(200,169,110,.2); }

  /* ── Analytics ── */
  .bd-analytics-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 2rem; }
  .bd-stat-card { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 1.5rem; position: relative; overflow: hidden; }
  .bd-stat-icon { font-size: 1.5rem; margin-bottom: .75rem; }
  .bd-stat-value { font-size: 1.8rem; font-weight: 700; color: #fff; line-height: 1; margin-bottom: .3rem; }
  .bd-stat-label { font-size: .72rem; color: var(--text-dim); text-transform: uppercase; letter-spacing: .07em; }
  .bd-stat-delta { position: absolute; top: 1.25rem; right: 1.25rem; font-size: .72rem; font-weight: 700; color: #6ee7b7; background: rgba(16,185,129,.1); padding: .15rem .5rem; border-radius: 8px; }
  .bd-analytics-chart { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 1.75rem; }
  .bd-chart-label { font-size: .82rem; color: var(--text-dim); margin-bottom: 1.5rem; font-weight: 600; }
  .bd-bar-chart { display: flex; align-items: flex-end; gap: .6rem; height: 130px; }
  .bd-bar-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: .4rem; }
  .bd-bar-col span { font-size: .62rem; color: var(--text-dim); }
  .bd-bar { width: 100%; background: linear-gradient(to top, var(--gold), rgba(200,169,110,.5)); border-radius: 6px 6px 0 0; min-height: 4px; display: flex; align-items: flex-start; justify-content: center; font-size: .6rem; color: rgba(0,0,0,.4); padding-top: .2rem; }

  /* ── Plans ── */
  .bd-plans { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 1.5rem; }
  .bd-plan-card { background: var(--surface); border: 1px solid var(--border); border-radius: 20px; padding: 2rem; position: relative; }
  .bd-plan-card.highlight { border-color: var(--gold); }
  .bd-plan-badge { position: absolute; top: 1rem; right: 1rem; background: var(--gold); color: #fff; font-size: .65rem; font-weight: 700; padding: .2rem .6rem; border-radius: 10px; text-transform: uppercase; }
  .bd-plan-current { background: rgba(16,185,129,.1); color: #6ee7b7; border: 1px solid rgba(16,185,129,.2); font-size: .65rem; font-weight: 700; padding: .2rem .6rem; border-radius: 10px; display: inline-block; margin-bottom: .75rem; }
  .bd-plan-name { font-family: 'Cormorant Garamond', serif; font-size: 1.4rem; color: #fff; margin-bottom: .5rem; }
  .bd-plan-price { font-size: 2.5rem; font-weight: 700; color: var(--gold); margin-bottom: .25rem; }
  .bd-plan-price span { font-size: 1rem; color: var(--text-dim); font-weight: 400; }
  .bd-plan-features { list-style: none; display: flex; flex-direction: column; gap: .55rem; margin: 1.5rem 0; }
  .bd-plan-features li { font-size: .85rem; color: var(--text-mid); }
  .bd-plan-features li::before { content: '✓ '; color: var(--gold); font-weight: 700; }
  .bd-plan-btn { width: 100%; padding: .85rem; border-radius: 12px; border: 1px solid var(--border); background: none; color: var(--text-mid); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .9rem; transition: all .2s; }
  .bd-plan-btn.primary { background: var(--gold); border-color: var(--gold); color: #fff; font-weight: 700; }
  .bd-plan-btn:hover:not(.primary) { background: var(--surface); color: #fff; }

  /* ── Account ── */
  .bd-account-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
  @media (max-width: 700px) { .bd-account-grid { grid-template-columns: 1fr; } }
  .bd-account-card { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 1.75rem; display: flex; flex-direction: column; gap: .85rem; }
  .bd-account-card h3 { font-family: 'Cormorant Garamond', serif; font-size: 1.2rem; color: #fff; margin-bottom: .25rem; }
  .bd-field-label { font-size: .72rem; color: var(--text-dim); text-transform: uppercase; letter-spacing: .07em; margin-top: .25rem; }
  .bd-save-btn { margin-top: .5rem; padding: .8rem 1.5rem; background: var(--gold); border: none; border-radius: 10px; color: #fff; font-family: 'DM Sans', sans-serif; font-size: .9rem; font-weight: 700; cursor: pointer; }

  /* ── Inputs ── */
  .bd-card { background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.09); border-radius: 16px; padding: 1.5rem; max-width: 560px; }
  .bd-payout-card { display: flex; flex-direction: column; gap: 1rem; }
  .bd-payout-banner { background: rgba(200,169,110,.1); border: 1px solid rgba(200,169,110,.28); border-radius: 12px; padding: .9rem 1.1rem; color: #e9d8b8; font-size: .85rem; margin-bottom: 1.25rem; max-width: 560px; }
  .bd-btn { padding: .8rem 1.5rem; border-radius: 10px; border: 1px solid rgba(255,255,255,.15); background: rgba(255,255,255,.06); color: var(--text); font-family: 'DM Sans', sans-serif; font-size: .9rem; font-weight: 700; cursor: pointer; align-self: flex-start; transition: all .2s; }
  .bd-btn.primary { background: var(--biz-accent, #c8a96e); border-color: var(--biz-accent, #c8a96e); color: #1a1a1a; }
  .bd-btn:disabled { opacity: .6; }
  .bd-plan-tagline { font-size: .78rem; color: rgba(255,255,255,.45); margin: -.4rem 0 .6rem; }
    .bd-input { background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.1); border-radius: 10px; padding: .75rem 1rem; color: var(--text); font-family: 'DM Sans', sans-serif; font-size: .9rem; outline: none; transition: border-color .2s; width: 100%; }
  .bd-input.sm { padding: .55rem .85rem; font-size: .85rem; }
  .bd-input:focus { border-color: var(--gold); }
  .bd-input.error { border-color: #ef4444; }
  .bd-err { font-size: .75rem; color: #fca5a5; margin-top: .2rem; display: block; }
  .req { color: #fca5a5; }

  /* ── Modal overlay ── */
  .bd-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.75); backdrop-filter: blur(8px); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 1rem; }
  .bd-modal { background: #111; border: 1px solid rgba(255,255,255,.1); border-radius: 24px; width: 100%; max-width: 580px; max-height: 90vh; overflow-y: auto; box-shadow: 0 32px 80px rgba(0,0,0,.7); animation: modalIn .25s ease; }
  .bd-modal.wide { max-width: 760px; }
  @keyframes modalIn { from { transform: scale(.96) translateY(8px); opacity: 0; } to { transform: none; opacity: 1; } }
  .bd-modal-header { display: flex; justify-content: space-between; align-items: flex-start; padding: 1.75rem 1.75rem 0; }
  .bd-modal-title-area { display: flex; align-items: flex-start; gap: .85rem; }
  .bd-modal-icon { font-size: 2rem; flex-shrink: 0; margin-top: .1rem; }
  .bd-modal-title-area h2 { font-family: 'Cormorant Garamond', serif; font-size: 1.5rem; color: #fff; margin-bottom: .2rem; }
  .bd-modal-title-area p { font-size: .78rem; color: var(--text-dim); line-height: 1.5; }
  .bd-modal-close { background: none; border: 1px solid var(--border); border-radius: 8px; color: var(--text-dim); cursor: pointer; font-size: 1rem; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all .2s; }
  .bd-modal-close:hover { border-color: #ef4444; color: #fca5a5; }
  .bd-modal-body { padding: 1.5rem 1.75rem 1.75rem; display: flex; flex-direction: column; gap: 1.1rem; }

  /* ── Step indicator ── */
  .bd-steps { display: flex; align-items: center; padding: 1.25rem 1.75rem 0; position: relative; gap: 0; }
  .bd-steps-line { position: absolute; left: 2.75rem; right: 2.75rem; top: 50%; border-top: 1px dashed rgba(255,255,255,.1); z-index: 0; pointer-events: none; }
  .bd-step { display: flex; flex-direction: column; align-items: center; gap: .3rem; flex: 1; position: relative; z-index: 1; }
  .bd-step-dot { width: 28px; height: 28px; border-radius: 50%; background: #1a1a1a; border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; font-size: .75rem; color: var(--text-dim); transition: all .2s; }
  .bd-step.active .bd-step-dot { background: var(--gold); border-color: var(--gold); color: #fff; font-weight: 700; }
  .bd-step.done .bd-step-dot { background: rgba(16,185,129,.15); border-color: rgba(16,185,129,.4); color: #6ee7b7; }
  .bd-step span:last-child { font-size: .68rem; color: var(--text-dim); }
  .bd-step.active span:last-child { color: var(--gold); }

  /* ── Form fields ── */
  .bd-field { display: flex; flex-direction: column; gap: .4rem; }
  .bd-field label { font-size: .72rem; color: var(--text-dim); text-transform: uppercase; letter-spacing: .07em; }
  .bd-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: .75rem; }
  .bd-slug-row { display: flex; align-items: center; gap: 0; }
  .bd-slug-input { border-radius: 10px 0 0 10px !important; flex: 1; min-width: 0; }
  .bd-slug-suffix { background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.1); border-left: none; border-radius: 0 10px 10px 0; padding: .75rem .8rem; font-size: .78rem; color: var(--text-dim); white-space: nowrap; }
  .bd-slug-suffix.sm { padding: .55rem .65rem; font-size: .72rem; }
  .bd-slug-preview { font-size: .75rem; color: var(--gold); margin-top: .2rem; }

  /* ── Admin info banner ── */
  .bd-admin-info-banner { display: flex; align-items: flex-start; gap: .75rem; background: rgba(99,102,241,.07); border: 1px solid rgba(99,102,241,.2); border-radius: 12px; padding: 1rem 1.25rem; }
  .bd-admin-info-banner span { font-size: 1.5rem; flex-shrink: 0; }
  .bd-admin-info-banner p { font-size: .82rem; color: rgba(255,255,255,.5); line-height: 1.65; margin: 0; }
  .bd-admin-info-banner strong { color: rgba(255,255,255,.8); }

  /* ── Summary ── */
  .bd-summary { background: rgba(255,255,255,.03); border: 1px solid var(--border); border-radius: 12px; padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: .6rem; }
  .bd-summary-title { font-size: .7rem; text-transform: uppercase; letter-spacing: .08em; color: var(--text-dim); margin-bottom: .25rem; }
  .bd-summary-row { display: flex; justify-content: space-between; font-size: .85rem; }
  .bd-summary-row span { color: var(--text-dim); }
  .bd-summary-row strong { color: var(--text); }

  /* ── Modal actions ── */
  .bd-modal-actions { display: flex; gap: .75rem; justify-content: flex-end; margin-top: .5rem; }
  .bd-modal-cancel { padding: .7rem 1.4rem; border: 1px solid var(--border); border-radius: 22px; background: none; color: var(--text-dim); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .875rem; transition: all .2s; }
  .bd-modal-cancel:hover { border-color: rgba(255,255,255,.2); color: #fff; }
  .bd-modal-next { padding: .7rem 1.6rem; border: none; border-radius: 22px; background: rgba(255,255,255,.08); color: #fff; cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .875rem; font-weight: 600; transition: all .2s; }
  .bd-modal-next:hover { background: rgba(255,255,255,.14); }
  .bd-modal-save { padding: .75rem 1.75rem; border: none; border-radius: 22px; background: var(--gold); color: #fff; cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .9rem; font-weight: 700; transition: opacity .2s; box-shadow: 0 4px 16px var(--gold-glow); }
  .bd-modal-save:hover:not(:disabled) { opacity: .88; }
  .bd-modal-save:disabled { opacity: .45; cursor: not-allowed; }

  /* ── Branch Manager Modal specific ── */
  .bd-branch-how { display: flex; align-items: center; gap: .5rem; background: rgba(99,102,241,.06); border: 1px solid rgba(99,102,241,.15); border-radius: 14px; padding: 1.1rem 1.25rem; margin-bottom: .25rem; }
  .bd-branch-how-item { display: flex; align-items: flex-start; gap: .6rem; flex: 1; min-width: 0; }
  .bd-branch-how-item span { font-size: 1.4rem; flex-shrink: 0; }
  .bd-branch-how-item strong { display: block; font-size: .82rem; color: #fff; margin-bottom: .15rem; }
  .bd-branch-how-item span:last-child, .bd-branch-how-item > div > span { font-size: .75rem; color: var(--text-dim); line-height: 1.4; }
  .bd-branch-how-arrow { color: var(--text-dim); font-size: 1rem; flex-shrink: 0; padding: 0 .25rem; }
  .bd-branch-list { display: flex; flex-direction: column; gap: .5rem; }
  .bd-branch-item { background: var(--surface); border: 1px solid var(--border); border-radius: 14px; padding: 1rem 1.25rem; display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; }
  .bd-branch-item.main { border-color: rgba(200,169,110,.2); background: var(--gold-dim); }
  .bd-branch-item.inactive { opacity: .55; }
  .bd-branch-main-badge { display: inline-flex; align-items: center; font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: var(--gold); background: var(--gold-dim); border: 1px solid rgba(200,169,110,.25); border-radius: 8px; padding: .15rem .55rem; margin-right: .5rem; flex-shrink: 0; margin-top: .15rem; }
  .bd-branch-item-left { display: flex; align-items: flex-start; gap: .75rem; flex: 1; min-width: 0; }
  .bd-branch-order-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--border); border: 1px solid rgba(255,255,255,.2); flex-shrink: 0; margin-top: .4rem; }
  .bd-branch-name { font-size: .9rem; font-weight: 600; color: #fff; margin-bottom: .2rem; }
  .bd-branch-url { font-family: monospace; font-size: .72rem; color: var(--text-dim); margin-bottom: .2rem; word-break: break-all; }
  .bd-branch-addr { font-size: .75rem; color: var(--text-dim); margin-top: .1rem; }
  .bd-branch-item-right { display: flex; align-items: center; gap: .5rem; flex-shrink: 0; }
  .bd-branch-status { font-size: .72rem; font-weight: 700; color: #6ee7b7; white-space: nowrap; }
  .bd-branch-link { font-size: .75rem; color: var(--gold); text-decoration: none; white-space: nowrap; }
  .bd-branch-toggle { padding: .3rem .7rem; border-radius: 20px; border: 1px solid; cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .72rem; font-weight: 700; transition: all .2s; }
  .bd-branch-toggle.on { background: rgba(16,185,129,.08); border-color: rgba(16,185,129,.25); color: #6ee7b7; }
  .bd-branch-toggle.off { background: rgba(255,255,255,.04); border-color: var(--border); color: var(--text-dim); }
  .bd-branch-edit-btn { padding: .3rem .65rem; background: rgba(255,255,255,.06); border: 1px solid var(--border); border-radius: 8px; color: var(--text-dim); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .75rem; transition: all .2s; }
  .bd-branch-edit-btn:hover { color: #fff; border-color: rgba(255,255,255,.2); }
  .bd-branch-del-btn { width: 28px; height: 28px; background: rgba(239,68,68,.06); border: 1px solid rgba(239,68,68,.15); border-radius: 8px; color: #fca5a5; cursor: pointer; font-size: .85rem; display: flex; align-items: center; justify-content: center; transition: all .2s; }
  .bd-branch-del-btn:hover { background: rgba(239,68,68,.15); border-color: rgba(239,68,68,.35); }
  .bd-branch-edit-form { width: 100%; display: flex; flex-direction: column; gap: .75rem; }
  .bd-branch-edit-actions { display: flex; gap: .5rem; }
  .bd-branch-save-btn { padding: .45rem 1.1rem; background: var(--gold); border: none; border-radius: 20px; color: #fff; cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .8rem; font-weight: 700; transition: opacity .2s; }
  .bd-branch-save-btn:disabled { opacity: .5; cursor: not-allowed; }
  .bd-branch-cancel-btn { padding: .45rem 1rem; background: none; border: 1px solid var(--border); border-radius: 20px; color: var(--text-dim); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .8rem; transition: all .2s; }
  .bd-branch-add-form { background: rgba(200,169,110,.05); border: 1px dashed rgba(200,169,110,.25); border-radius: 14px; padding: 1.25rem; display: flex; flex-direction: column; gap: .85rem; }
  .bd-branch-add-form-title { font-size: .82rem; font-weight: 700; color: var(--gold); }
  .bd-add-branch-btn { padding: .75rem 1rem; background: none; border: 1px dashed rgba(255,255,255,.12); border-radius: 14px; color: var(--text-dim); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .85rem; transition: all .2s; text-align: center; }
  .bd-add-branch-btn:hover { border-color: rgba(200,169,110,.35); color: var(--gold); background: var(--gold-dim); }

  /* ── Landing page preview ── */
  .bd-landing-preview { background: rgba(255,255,255,.02); border: 1px solid var(--border); border-radius: 16px; padding: 1.5rem; }
  .bd-landing-preview-title { font-size: .8rem; font-weight: 700; color: var(--text-dim); text-transform: uppercase; letter-spacing: .08em; margin-bottom: .4rem; }
  .bd-landing-preview-sub { font-size: .78rem; color: var(--text-dim); margin-bottom: 1rem; line-height: 1.5; }
  .bd-landing-preview-buttons { display: flex; flex-direction: column; gap: .5rem; }
  .bd-preview-btn { display: flex; justify-content: space-between; align-items: center; padding: .85rem 1.25rem; background: rgba(255,255,255,.04); border: 1px solid; border-radius: 14px; font-size: .88rem; color: rgba(255,255,255,.8); font-family: 'DM Sans', sans-serif; }
  .bd-preview-btn.branch { padding: .75rem 1.25rem; }
  .bd-preview-btn-top { font-size: .82rem; color: rgba(255,255,255,.55); margin-bottom: .15rem; }
  .bd-preview-btn-branch { font-size: .9rem; font-weight: 700; }
  .bd-preview-btn-arrow { font-size: 1.1rem; margin-left: .5rem; }

  /* ── Owner action row on restaurant card ── */
  .bd-rest-owner-actions { display: flex; gap: .4rem; margin-top: .5rem; }
  .bd-owner-action-btn { flex: 1; padding: .5rem; border-radius: 8px; border: 1px solid var(--border); background: none; color: var(--text-dim); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .75rem; font-weight: 600; transition: all .2s; }
  .bd-owner-action-btn:hover { background: var(--surface); color: #fff; border-color: rgba(255,255,255,.2); }
  .bd-owner-action-btn.danger { color: rgba(239,68,68,.7); border-color: rgba(239,68,68,.15); }
  .bd-owner-action-btn.danger:hover { background: rgba(239,68,68,.1); color: #fca5a5; border-color: rgba(239,68,68,.35); }
  /* Status toggle */
  .bd-status-toggle { padding: .5rem 1rem; border-radius: 10px; border: 1px solid var(--border); background: none; color: var(--text-dim); cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: .82rem; transition: all .2s; }
  .bd-status-toggle.active { background: var(--gold-dim); border-color: rgba(200,169,110,.3); color: var(--gold); }

  /* ── Responsive ── */
  @media (max-width: 768px) {
    .bd-sidebar { display: none; }
    .bd-rest-grid { grid-template-columns: 1fr; }
    .bd-page { padding: 1.5rem 1rem; }
    .bd-page-header { flex-direction: column; }
    .bd-modal { margin: .5rem; border-radius: 16px; }
    .bd-branch-how { flex-direction: column; }
    .bd-branch-how-arrow { transform: rotate(90deg); }
    .bd-field-row { grid-template-columns: 1fr; }
  }
`;
